import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import SafeIcon from '../components/common/SafeIcon';
import { useToast } from '../components/ui/Toast';
import useWorkspace from '../hooks/useWorkspace';
import * as FiIcons from 'react-icons/fi';

const { FiBookOpen, FiEdit3, FiFeather, FiSearch, FiTarget } = FiIcons;
const TYPES = ['note', 'idea', 'reflection', 'reframe'];

const labelType = (type) => (
  type ? `${type.charAt(0).toUpperCase()}${type.slice(1)}` : 'Note'
);

const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Date unavailable'
    : date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

function splitReframe(text = '') {
  const marker = '\n\nKinder alternative:\n';
  const [original, alternative = ''] = text.split(marker);

  return {
    original: original.replace(/^Original thought:\n/, ''),
    alternative,
  };
}

function EntryForm({ entry, onSave, onCancel }) {
  const [type, setType] = useState(entry?.type || 'note');
  const textRef = useRef(null);
  const kinderRef = useRef(null);
  const reframe = splitReframe(entry?.text);

  const submit = (event) => {
    event.preventDefault();
    const text = textRef.current?.value.trim();
    const kinder = kinderRef.current?.value.trim();

    if (!text || (type === 'reframe' && !kinder)) return;

    onSave({
      type,
      text: type === 'reframe'
        ? `Original thought:\n${text}\n\nKinder alternative:\n${kinder}`
        : text,
    });

    if (!entry) {
      event.currentTarget.reset();
      setType('note');
    }
  };

  return (
    <form onSubmit={submit} className="p-6 space-y-5">
      <div>
        <label htmlFor={`entry-type-${entry?.id || 'new'}`} className="block mb-2 text-sm font-bold text-pine">
          Entry type
        </label>
        <select
          id={`entry-type-${entry?.id || 'new'}`}
          value={type}
          onChange={(event) => setType(event.target.value)}
          className="w-full rounded-2xl border border-pine/15 bg-paper px-4 py-3 text-pine focus:outline-none focus:ring-2 focus:ring-sage"
        >
          {TYPES.map((item) => (
            <option key={item} value={item}>{labelType(item)}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={`entry-text-${entry?.id || 'new'}`} className="block mb-2 text-sm font-bold text-pine">
          {type === 'reframe' ? 'Original thought' : 'Entry'}
        </label>
        <textarea
          id={`entry-text-${entry?.id || 'new'}`}
          ref={textRef}
          required
          autoFocus
          rows={5}
          maxLength={2000}
          defaultValue={entry?.type === 'reframe' ? reframe.original : entry?.text || ''}
          placeholder="Write what is on your mind."
          className="w-full resize-y rounded-2xl border border-pine/15 bg-paper px-4 py-3 text-pine focus:outline-none focus:ring-2 focus:ring-sage"
        />
      </div>

      {type === 'reframe' && (
        <div>
          <label htmlFor={`entry-kinder-${entry?.id || 'new'}`} className="block mb-2 text-sm font-bold text-pine">
            Kinder alternative
          </label>
          <textarea
            id={`entry-kinder-${entry?.id || 'new'}`}
            ref={kinderRef}
            required
            rows={4}
            maxLength={2000}
            defaultValue={entry?.type === 'reframe' ? reframe.alternative : ''}
            placeholder="What is a fairer or more helpful way to hold it?"
            className="w-full resize-y rounded-2xl border border-pine/15 bg-paper px-4 py-3 text-pine focus:outline-none focus:ring-2 focus:ring-sage"
          />
          <p className="mt-2 text-xs text-pine/55">
            A reframe is a writing prompt, not a diagnosis.
          </p>
        </div>
      )}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        )}
        <Button type="submit">{entry ? 'Save changes' : 'Save entry'}</Button>
      </div>
    </form>
  );
}

export default function ThoughtGarden() {
  const { notes, addNote, updateNote, addTask, storageError } = useWorkspace();
  const { success, error } = useToast();
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);

  const entries = useMemo(() => {
    const search = query.trim().toLowerCase();

    return [...notes]
      .filter((entry) => filter === 'all' || entry.type === filter)
      .filter((entry) => !search || entry.text.toLowerCase().includes(search))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [filter, notes, query]);

  const saveEntry = (values) => {
    addNote(values);
    success('Entry saved.');
  };

  const saveEdit = (values) => {
    updateNote(editing.id, values);
    setEditing(null);
    success('Entry updated.');
  };

  const makeTask = (entry) => {
    if (entry.linkedTaskId) return;

    const title = splitReframe(entry.text).original.split('\n')[0].trim().slice(0, 120);
    if (!title) {
      error('Add some text before making a task.');
      return;
    }

    const task = addTask({
      title,
      status: 'todo',
      project: 'Journal',
      scheduledDate: '',
      scheduledTime: '',
      estimatedMinutes: 25,
    });

    updateNote(entry.id, { linkedTaskId: task.id });
    success('Task created from this idea.');
  };

  return (
    <div className="max-w-6xl mx-auto space-y-7">
      <header>
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-sage">
          Your effort, made visible.
        </p>
        <h1 className="text-4xl lg:text-5xl font-cormorant font-bold text-pine">
          Make space for your thoughts.
        </h1>
        <p className="mt-2 max-w-2xl text-pine/65">
          Capture a note, develop an idea, or find a kinder way to hold a difficult thought.
        </p>
      </header>

      {storageError && (
        <p role="alert" className="rounded-2xl bg-crimson/10 px-4 py-3 text-sm text-crimson">
          {storageError}
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <Card className="lg:col-span-5 overflow-hidden">
          <div className="flex items-center gap-2 px-6 pt-6">
            <SafeIcon icon={FiFeather} className="text-sage" />
            <h2 className="text-2xl font-cormorant font-bold text-pine">New entry</h2>
          </div>
          <EntryForm onSave={saveEntry} />
        </Card>

        <section className="lg:col-span-7 space-y-4" aria-labelledby="entries-title">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h2 id="entries-title" className="text-2xl font-cormorant font-bold text-pine">
              Entries
            </h2>
            <div className="flex gap-2">
              <label className="relative flex-1">
                <span className="sr-only">Search entries</span>
                <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-pine/40" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search"
                  className="w-full rounded-2xl border border-pine/15 bg-white py-2.5 pl-10 pr-3 text-sm text-pine focus:outline-none focus:ring-2 focus:ring-sage"
                />
              </label>
              <label>
                <span className="sr-only">Filter by type</span>
                <select
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  className="rounded-2xl border border-pine/15 bg-white px-3 py-2.5 text-sm text-pine focus:outline-none focus:ring-2 focus:ring-sage"
                >
                  <option value="all">All types</option>
                  {TYPES.map((item) => (
                    <option key={item} value={item}>{labelType(item)}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {entries.length ? entries.map((entry) => (
            <Card key={entry.id} className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <span className="rounded-full bg-sage/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-pine">
                    {labelType(entry.type)}
                  </span>
                  <p className="mt-3 whitespace-pre-wrap break-words leading-relaxed text-pine">
                    {entry.text}
                  </p>
                  <p className="mt-3 text-xs text-pine/45">{formatDate(entry.createdAt)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(entry)}
                  aria-label={`Edit ${labelType(entry.type)} entry`}
                  className="rounded-xl p-2 text-pine/50 hover:bg-pine/5 focus:outline-none focus:ring-2 focus:ring-sage"
                >
                  <SafeIcon icon={FiEdit3} />
                </button>
              </div>

              {entry.type === 'idea' && (
                <div className="mt-4 border-t border-pine/10 pt-4">
                  {entry.linkedTaskId ? (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <span className="inline-flex items-center gap-2 text-sm font-bold text-sage">
                        <SafeIcon icon={FiTarget} /> Task created
                      </span>
                      <div className="flex gap-3 text-sm font-bold text-pine">
                        <Link to="/plan">Plan</Link>
                        <Link to={`/studio?task=${encodeURIComponent(entry.linkedTaskId)}`}>
                          Focus
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => makeTask(entry)}>
                      Make a task
                    </Button>
                  )}
                </div>
              )}
            </Card>
          )) : (
            <Card className="p-10 text-center border-dashed">
              <SafeIcon icon={FiBookOpen} className="mx-auto mb-3 h-8 w-8 text-sage" />
              <h3 className="text-xl font-cormorant font-bold text-pine">
                {notes.length ? 'No entries match this view.' : 'Your journal is open.'}
              </h3>
              <p className="mt-1 text-sm text-pine/60">
                {notes.length ? 'Try another type or search phrase.' : 'Write your first entry when you are ready.'}
              </p>
            </Card>
          )}
        </section>
      </div>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title="Edit entry" size="lg">
        {editing && (
          <EntryForm
            key={editing.id}
            entry={editing}
            onSave={saveEdit}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>
    </div>
  );
}